import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useRoom } from '../components/RoomContext';
import { Users, Plus, Share2, Check, Copy, Loader, AlertCircle, UserPlus, CircleDashed, LogOut } from 'lucide-react';
import { subscribeRoomDemoCollection, socket } from '../services/frontendDemoAPI';

const apiUrl = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

export default function Room() {
  const { roomId } = useParams();
  const { room, username, joinRoom, setUsername, leaveRoom } = useRoom();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingAdd, setPendingAdd] = useState(false);
  const [showAddToast, setShowAddToast] = useState(false);
  const [error, setError] = useState('');
  const [shareSuccess, setShareSuccess] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [roomMembers, setRoomMembers] = useState([]);
  const [showNameInput, setShowNameInput] = useState(false);
  const [nameInput, setNameInput] = useState('');

  // Username prompt logic: only managed here!
  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(`room_${roomId}_user`));
    } catch (e) {
      stored = null;
    }
    if (!stored || !stored.username) {
      setShowNameInput(true);
      setUsername('');
    } else {
      setUsername(stored.username);
      setShowNameInput(false);
    }
    // eslint-disable-next-line
  }, [roomId]); // setUsername is stable

  // Save username to localStorage whenever it changes and is not empty
  useEffect(() => {
    if (username) {
      localStorage.setItem(`room_${roomId}_user`, JSON.stringify({ username }));
    }
  }, [username, roomId]);

  // Fetch room records
  const fetchRecords = useCallback(async () => {
    if (!username) return;

    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/room/${roomId}/demo_collection`);
      if (!res.ok) throw new Error('Failed to fetch records');
      setRecords(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [username, roomId]);

  // Fetch room members from room_members collection
  const fetchMembers = useCallback(async () => {
    try {
      if (!username) return;
      const res = await fetch(`${apiUrl}/api/room/${roomId}/members`);
      if (!res.ok) throw new Error('Failed to fetch room members');
      const data = await res.json();
      setRoomMembers(data);

    } catch (err) {
      // Optionally handle error
    }
  }, [username, roomId]);

  // Main effect: fetch data and set up subscription
  useEffect(() => {
    let ignore = false;
    setLoading(true);

    fetchRecords().then(() => { if (!ignore) setLoading(false); });
    fetchMembers();

    // Real-time updates for this room
    const unsubscribe = subscribeRoomDemoCollection(roomId, (newRecords) => {
      if (!ignore) {
        setRecords(newRecords);
        fetchMembers();
      }
    });

    return () => {
      ignore = true;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [fetchRecords, fetchMembers, roomId]);

  // Real-time updates for room members
  useEffect(() => {
    if (!roomId) return;
    if (!username) return;
    const event = `room_members_update_${roomId}`;
    const handler = (members) => setRoomMembers(members);
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, [roomId, username]);

  // Rehydrate context if missing (room is null but username and roomId are present)
  React.useEffect(() => {
    if (!room && username && roomId) {
      fetch(`${apiUrl}/api/room/${roomId}`)
        .then(res => res.json())
        .then(roomData => {
          if (roomData && roomData.id) {
            joinRoom(roomData, username);
          }
        });
    }
    // eslint-disable-next-line
  }, [room, username, roomId]);

  // Join room with name
  const handleJoinRoom = async (e) => {
    e.preventDefault();
    if (!nameInput.trim()) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/room/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: nameInput.trim() })
      });
      if (!res.ok) {
        let msg = 'Failed to join room';
        try {
          const errData = await res.json();
          msg = errData?.error || msg;
        } catch {}
        throw new Error(msg);
      }
      // Always fetch the latest members after join
      await fetchMembers();
      const roomData = await res.json();
      joinRoom(roomData, nameInput.trim());
      setShowNameInput(false);
      setUsername(nameInput.trim());
      setNameInput('');
      setRoomMembers(roomData.members || []);
      // localStorage is now handled by the useEffect above
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Add record (order)
  const handleAdd = useCallback(async () => {
    setPendingAdd(true);
    setError('');
    try {
      await fetch(`${apiUrl}/api/room/${roomId}/demo_collection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `${username}'s order`,
          description: 'Demo description',
          room: roomId
        })
      });
      // Let the subscription update the records automatically!
      setShowAddToast(true);
      setTimeout(() => setShowAddToast(false), 1300);
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingAdd(false);
    }
  }, [roomId, username]);

  // Sharing logic
  const handleShare = async () => {
    const link = `${window.location.origin}/room/${roomId}`;
    const text = `Kom mee doen via ${link}!`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Room Invite', text, url: link });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShareSuccess(true);
      setTimeout(() => setShareSuccess(false), 2000);
    } catch {}
  };

  // Copy link logic
  const handleCopy = async () => {
    const link = `${window.location.origin}/room/${roomId}?join=1`;
    const text = `Kom mee doen via ${link}!`;
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {}
  };

  // Add this handler for leaving the room
  const handleLeaveRoom = async () => {
    console.log('[Room.js] handleLeaveRoom called', { room, username });
    try {
      await leaveRoom();
      console.log('[Room.js] leaveRoom finished');
      window.location.reload();
    } catch (err) {
      console.error('[Room.js] leaveRoom error', err);
    }
  };

  // Top-level loading/error screen only here
  if (loading) {
    return (
        <div className="flex flex-col items-center justify-center h-40 text-gray-500">
          <Loader className="w-8 h-8 mb-2 animate-spin" />
          <span>Laden…</span>
        </div>
    );
  }

  if (error) {
    return (
        <div className="flex flex-col items-center justify-center h-40 text-red-600">
          <AlertCircle className="w-8 h-8 mb-2" />
          <span className="text-sm">Error: {error}</span>
        </div>
    );
  }

  if (showNameInput) {
    return (
        <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow mt-8">
          <div className="text-center mb-6">
            <Users className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800">Welkom bij de gezamenlijke room!</h1>
            <p className="text-gray-600 mt-2">Voer je naam in om deel te nemen</p>
          </div>
          <form onSubmit={handleJoinRoom} className="space-y-4">
            <input
                type="text"
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Je naam..."
                className="w-full px-4 py-3 border-2 border-green-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-200"
                required
                autoFocus
            />
            <button
                type="submit"
                className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer w-full justify-center"
            >
              <UserPlus size={18} />
              Deelnemen aan room
            </button>
          </form>
          {error && <div className="text-red-600 mt-4">{error}</div>}
        </div>
    );
  }

  return (
      <div className="max-w-md mx-auto p-4">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-6 h-6 text-green-600" />
          <h1 className="text-lg font-bold">Room: {roomId}</h1>
        </div>
        <div className="mb-4">
          <div className="font-semibold mb-1">Deelnemers in deze room:</div>
          <ul className="flex flex-wrap gap-2">
            {roomMembers.map((m) => (
                <li key={m.username} className={`px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1 border border-green-300 ${m.username === username ? 'border-2 border-green-600' : ''}`}>
                  <Check className="w-3 h-3" />
                  {m.username} {m.username === username && <span className="ml-1">(jij)</span>}
                </li>
            ))}
          </ul>
        </div>
        <div className="flex gap-2 mb-4 flex-wrap">
          <button
              className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer"
              onClick={handleShare}
          >
            <Share2 className="w-4 h-4" />
            Deel deze room
          </button>
          <button
              className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer"
              onClick={handleCopy}
          >
            <Copy className="w-4 h-4" />
            Kopieer link
          </button>
          {shareSuccess && <span className="text-green-600 flex items-center gap-1"><Check className="w-4 h-4" /> Gedeeld!</span>}
          {copySuccess && <span className="text-green-600 flex items-center gap-1"><Check className="w-4 h-4" /> Gekopieerd!</span>}
        </div>
        {/* Leave Room Button */}
        <button
          className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer mb-4"
          onClick={handleLeaveRoom}
        >
          <LogOut size={18} className="mr-1" />
          Leave Room
        </button>
        <div className="mb-4 text-gray-700">Welkom, <span className="font-semibold">{username}</span>! Deel deze link met anderen om samen te werken.</div>
        <button
            className="px-4 py-2 text-sm bg-green-100 text-green-600 border-2 border-green-600 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2 mx-auto cursor-pointer mb-4"
            onClick={handleAdd}
            disabled={pendingAdd}
        >
          {pendingAdd
              ? <><CircleDashed className="w-4 h-4 animate-spin" /> Toevoegen…</>
              : <><Plus className="w-4 h-4" /> Voeg record toe</>
          }
        </button>

        {/* Toast notification for smooth feedback */}
        {showAddToast && (
            <div className="fixed left-1/2 bottom-8 z-50 -translate-x-1/2 bg-green-600 text-white rounded-lg shadow-lg px-4 py-2 flex items-center gap-2 text-sm animate-fade-in">
              <Check className="w-4 h-4" />
              Record toegevoegd!
            </div>
        )}

        <ul className="space-y-3">
          {records.map((record) => (
              <li key={record.id} className="p-4 bg-white rounded-md shadow border border-gray-200 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-gray-900 text-base">{record.title}</span>
                </div>
                <span className="text-gray-700 text-sm">{record.description}</span>
                <span className="text-xs text-gray-400">ID: {record.id}</span>
              </li>
          ))}
        </ul>
      </div>
  );
}
